import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Image,
    Input,
    Link,
    Stack,
    Text,
    Flex,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import {pxToRem} from '../../utills/pxToRem'
import {fonts} from '../../theme/foundations/fonts'
import {DRAWER_SOCIAL_ICONS} from '../../constants'
import {formErrorMessageStyle} from '../register/index.style'

const drawerLoginBoxStyles = {
    brandLogo: {
        height: [pxToRem(41), pxToRem(41), pxToRem(53), pxToRem(53)]
    },
    loyaltyHeader: {
        color: 'black',
        fontFamily: fonts.primary,
        fontSize: [pxToRem(18), pxToRem(18), pxToRem(22), pxToRem(22)],
        letterSpacing: pxToRem(3),
        lineHeight: 'normal',
        fontWeight: '700',
        textTransform: 'uppercase',
        marginTop: `${pxToRem(20)} !important`,
        marginBottom: '0 !important'
    },
    loyaltyDescription: {
        fontFamily: fonts.primary,
        fontSize: pxToRem(12),
        fontWeight: '400',
        fontStyle: 'normal',
        lineHeight: 'normal',
        letterSpacing: pxToRem(1),
        color: 'black',
        textAlign: 'center',
        my: `${pxToRem(20)} !important`
    },
    formLabel: {
        fontFamily: fonts.primary,
        fontSize: pxToRem(14),
        fontWeight: '400',
        letterSpacing: pxToRem(1),
        lineHeight: 'normal',
        color: 'black',
        marginBottom: pxToRem(5)
    },
    formInput: {
        borderColor: 'black',
        borderRadius: 0,
        fontFamily: fonts.primary,
        fontSize: pxToRem(16),
        letterSpacing: pxToRem(1),
        height: pxToRem(35),
        marginBottom: pxToRem(10),
        _focus: {
            borderColor: 'black',
            boxShadow: 'none',
            outline: 'none'
        },
        _focusVisible: {
            borderColor: 'black',
            boxShadow: 'none',
            outline: 'none'
        }
    },
    submitButton: {
        height: pxToRem(40),
        paddingTop: pxToRem(12),
        paddingBottom: pxToRem(12),
        paddingLeft: pxToRem(20),
        paddingRight: pxToRem(20),
        color: 'white',
        backgroundColor: 'cav_red',
        fontFamily: fonts.primary,
        fontSize: pxToRem(12),
        fontWeight: 700,
        lineHeight: pxToRem(13.5),
        letterSpacing: pxToRem(1.5),
        textTransform: 'uppercase',
        borderRadius: 0,
        margin: '0 !important',
        '&:hover': {
            color: 'cav_red',
            backgroundColor: 'white',
            border: `${pxToRem(1)} solid`
        },
        '&:disabled': {
            backgroundColor: 'cav_red',
            '&:hover': {
                color: 'white',
                backgroundColor: 'cav_red'
            }
        }
    }
}

const DrawerLoginBox = ({
    onClose,
    onOpenAuthModal,
    isRegistered = false,
    onSignout,
    onInputBlur
}) => {
    const form = useForm()
    const {formatMessage} = useIntl()
    const theme = useTheme()
    const colors = theme.colors

    const validationRules = {
        required: formatMessage({
            defaultMessage: 'Please enter your email address.',
            id: 'use_login_fields.error.required_email'
        }),
        pattern: {
            value: /^\S+@\S+\.\S+$/,
            message: formatMessage({
                defaultMessage: 'Please enter valid email address.',
                id: 'use_login_fields.error.valid_email'
            })
        }
    }

    const handleSubmit = (data) => {
        if (onClose) onClose()
        if (onOpenAuthModal) onOpenAuthModal(data.email)
    }

    const onFormSubmit = form.handleSubmit(handleSubmit)

    return (
        <Box sx={{mt: pxToRem(30)}}>
            <Stack justify="center" align="center" spacing={4}>
                <Image
                    sx={drawerLoginBoxStyles.brandLogo}
                    src={getAssetUrl('static/img/global/My_Cavenders-Logo.png')}
                />
                <Text sx={drawerLoginBoxStyles.loyaltyHeader} as="h2" align="center">
                    <FormattedMessage
                        defaultMessage="Loyalty is rewarded here"
                        id="register_form.heading.loyalty_is_rewarded_here"
                    />
                </Text>
            </Stack>
            {!isRegistered ? (
                <Text sx={drawerLoginBoxStyles.loyaltyDescription}>
                    <FormattedMessage
                        defaultMessage="Joining is free. Earn 1 point for every dollar you spend on purchases. For every 200 points receive a $5 reward."
                        id="drawer_menu.loyalty_description"
                    />
                </Text>
            ) : (
                <Box sx={{pt: pxToRem(20)}}></Box>
            )}
            {!isRegistered ? (
                <Box as="form" marginTop={pxToRem(20)} onSubmit={onFormSubmit} noValidate>
                    <FormControl>
                        <FormLabel {...drawerLoginBoxStyles.formLabel}>
                            <FormattedMessage
                                defaultMessage="Enter Your Email"
                                id="drawer_menu.label.enter_your_email"
                            />
                            <span color="cav_calltoaction_darkred">*</span>
                        </FormLabel>
                        <Input
                            type="text"
                            placeholder="example@email.com"
                            {...form.register('email', validationRules)}
                            onBlur={(e) => {
                                form.trigger('email')
                                if (onInputBlur) onInputBlur(e)
                            }}
                            sx={{
                                ...drawerLoginBoxStyles.formInput,
                                borderColor: form.formState.errors.email
                                    ? 'cav_error_red'
                                    : 'black',
                                ...(form.formState.errors.email && {
                                    _focus: {
                                        backgroundColor: colors.whiteRed[20],
                                        borderColor: 'cav_error_red',
                                        boxShadow: 'none',
                                        outline: 'none'
                                    },
                                    _focusVisible: {
                                        backgroundColor: colors.whiteRed[20],
                                        borderColor: 'cav_error_red',
                                        boxShadow: 'none',
                                        outline: 'none'
                                    }
                                })
                            }}
                        />
                        {form.formState.errors.email && (
                            <Flex
                                flexDirection="row"
                                alignItems="center"
                                gap="5px"
                                mt={{base: '5px', md: '10px'}}
                            >
                                <img
                                    src={getAssetUrl('static/img/header/error-symbol.svg')}
                                    alt="Error symbol"
                                    style={{
                                        width: '13px',
                                        height: '13px',
                                        flexShrink: 0,
                                        alignSelf: 'start'
                                    }}
                                />
                                <Text
                                    sx={{...formErrorMessageStyle, marginTop: 0, marginBottom: 0}}
                                >
                                    {form.formState.errors.email.message}
                                </Text>
                            </Flex>
                        )}
                    </FormControl>
                    <Button
                        type="submit"
                        width="100%"
                        marginTop={`${pxToRem(10)} !important`}
                        sx={drawerLoginBoxStyles.submitButton}
                        isLoading={form.formState.isSubmitting}
                    >
                        <FormattedMessage
                            defaultMessage="Sign In/Sign Up"
                            id="login_form.button.signInSignUp"
                        />{' '}
                        {' >>'}
                    </Button>
                </Box>
            ) : (
                <Button
                    onClick={onSignout}
                    width="100%"
                    marginTop={pxToRem(20)}
                    sx={drawerLoginBoxStyles.submitButton}
                >
                    <FormattedMessage defaultMessage="Sign Out" id="drawer_menu.button.sign_out" />{' '}
                    {' >>'}
                </Button>
            )}
            {/* Social icons */}
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={pxToRem(20)}
                marginTop={pxToRem(20)}
                marginBottom={pxToRem(14)}
            >
                {DRAWER_SOCIAL_ICONS.map((icon) => (
                    <Link
                        key={icon.alt}
                        href={icon.url}
                        isExternal
                        display="inline-flex"
                        alignItems="center"
                    >
                        <Image
                            src={getAssetUrl(icon.src)}
                            alt={icon.alt}
                            width={pxToRem(25)}
                            height={pxToRem(25)}
                            objectFit="contain"
                        />
                    </Link>
                ))}
            </Box>
        </Box>
    )
}

DrawerLoginBox.propTypes = {
    onClose: PropTypes.func,
    onOpenAuthModal: PropTypes.func,
    isRegistered: PropTypes.bool,
    onSignout: PropTypes.func,
    onInputBlur: PropTypes.func
}

export default DrawerLoginBox
